import type { InvoiceLineItem } from '../schemas/invoice.js';
import type { AgentTier } from '../schemas/agent.js';
import type { RecruiterTier } from '../schemas/recruiter.js';
import type { CommissionRules } from '../schemas/commission-rules.js';

/**
 * Pure commission-accrual math. Shared by the apps/functions
 * `commissionAccrue` Firestore trigger AND by client-side previews
 * ("if this invoice were paid today you'd earn RM X") in the agent PWA.
 *
 * No I/O, no Firebase types. Given an invoice's line items + the platform
 * rules + the agent's tier and per-agent overrides, returns a breakdown
 * of what the ledger entry should write.
 *
 * Math:
 *   subscription_part      = floor(sum(subscription lines) * recurringRate)
 *   extra_outlets_part     = floor(sum(extra_outlets lines) * perOutletRate)
 *   proration_part         = floor(sum(proration_addition lines) * perOutletRate)
 *   pre_multiplier_total   = subscription_part + extra_outlets_part + proration_part
 *   commission_total_sen   = floor(pre_multiplier_total * tierMultiplier)
 *
 * Rounding is `floor` so the platform never overpays the agent on
 * partial-sen amounts; the agent's view + the ledger always agree.
 *
 * Eligible line kinds:
 *   - 'subscription'         → recurringRate
 *   - 'extra_outlets'        → perOutletRate
 *   - 'proration_addition'   → perOutletRate (mid-month outlet additions)
 *
 * Ignored:
 *   - 'proration_removal' (negative; refund credits don't flip into commission)
 *   - 'credit', 'manual_adjustment' (super-admin one-offs don't fan out to agents)
 *   - any line with `amount <= 0` (defence-in-depth against negatives)
 *
 * Clawbacks on refund are handled separately by the dedicated `clawback`
 * function, NOT by this helper — this is the forward "accrue" path only.
 */

export interface CommissionInputRules {
  readonly recurringRate: number;
  readonly perExtraOutletRate: number;
  /** Per-tier multiplier. `Partial<Record>` because `z.record(enum, …)`
   *  inferred type is partial; missing keys fall back to 1 at use-site. */
  readonly tierMultipliers: Readonly<Partial<Record<AgentTier, number>>>;
}

export interface CommissionInputOverrides {
  readonly recurringRate?: number;
  readonly perOutletRate?: number;
  readonly tierMultiplier?: number;
}

export interface CommissionAccrual {
  /** Sum across components, integer sen, never negative. */
  readonly totalSen: number;
  /** Effective rate used for `subscription` lines (override beats default). */
  readonly effectiveRecurringRate: number;
  /** Effective rate used for `extra_outlets` + `proration_addition` lines. */
  readonly effectivePerOutletRate: number;
  /** Effective multiplier applied to the pre-multiplier subtotal. */
  readonly effectiveMultiplier: number;
  /** Per-line-kind breakdown for the ledger entry's `basis` snapshot. */
  readonly perKindSen: {
    readonly subscription: number;
    readonly extra_outlets: number;
    readonly proration_addition: number;
  };
}

export function computeCommissionAccrual(input: {
  readonly lineItems: readonly Pick<InvoiceLineItem, 'kind' | 'amount'>[];
  readonly rules: CommissionInputRules;
  readonly tier: AgentTier;
  readonly overrides?: CommissionInputOverrides;
}): CommissionAccrual {
  const { lineItems, rules, tier, overrides } = input;
  const recurringRate = overrides?.recurringRate ?? rules.recurringRate;
  const perOutletRate = overrides?.perOutletRate ?? rules.perExtraOutletRate;
  const multiplier = overrides?.tierMultiplier ?? rules.tierMultipliers[tier] ?? 1;

  let subscriptionSen = 0;
  let extraOutletsSen = 0;
  let prorationSen = 0;
  for (const li of lineItems) {
    if (li.amount <= 0) continue;
    switch (li.kind) {
      case 'subscription':
        subscriptionSen += Math.floor(li.amount * recurringRate);
        break;
      case 'extra_outlets':
        extraOutletsSen += Math.floor(li.amount * perOutletRate);
        break;
      case 'proration_addition':
        prorationSen += Math.floor(li.amount * perOutletRate);
        break;
      default:
        break;
    }
  }
  const preMultiplierSubtotal = subscriptionSen + extraOutletsSen + prorationSen;
  const totalSen = Math.floor(preMultiplierSubtotal * multiplier);

  return {
    totalSen,
    effectiveRecurringRate: recurringRate,
    effectivePerOutletRate: perOutletRate,
    effectiveMultiplier: multiplier,
    perKindSen: {
      subscription: subscriptionSen,
      extra_outlets: extraOutletsSen,
      proration_addition: prorationSen,
    },
  };
}

// ── Sprint 80: recruiter epic foundation ─────────────────────────────

/**
 * First-invoice accrual breakdown — the on-boarding revenue layer.
 *
 * When the operator pays their FIRST invoice, the `signup_fee` +
 * `activation_fee` line items fan out into commission accruals for
 * both the agent that signed up the operator AND the recruiter (if any)
 * that signed up that agent.
 *
 * Math (each kind independently):
 *   agent_signup_sen     = floor(signup_fee_total * firstInvoice.signupAgentSharePct)
 *   recruiter_signup_sen = floor(signup_fee_total * firstInvoice.signupRecruiterSharePct)
 *   agent_activation_sen     = floor(activation_fee_total * firstInvoice.activationAgentSharePct)
 *   recruiter_activation_sen = floor(activation_fee_total * firstInvoice.activationRecruiterSharePct)
 *
 * Multipliers (tier) apply on top of the AGENT + RECRUITER subtotals
 * independently:
 *   agent_total_sen     = floor((agent_signup_sen + agent_activation_sen) * agentTierMultiplier)
 *   recruiter_total_sen = floor((recruiter_signup_sen + recruiter_activation_sen) * recruiterTierMultiplier)
 *
 * Floor throughout so the platform never overpays.
 */

export interface FirstInvoiceAccrual {
  readonly agentSen: number;
  readonly recruiterSen: number;
  readonly perKindSen: {
    readonly agentFromSignup: number;
    readonly agentFromActivation: number;
    readonly recruiterFromSignup: number;
    readonly recruiterFromActivation: number;
  };
}

export function computeFirstInvoiceAccrual(input: {
  readonly lineItems: readonly Pick<InvoiceLineItem, 'kind' | 'amount'>[];
  readonly rules: CommissionRules;
  readonly agentTier: AgentTier;
  /** Set only when an operator's agent has a recruiter on file. */
  readonly recruiterTier?: RecruiterTier;
  /** Per-agent override of the agent tier multiplier (optional). */
  readonly agentTierMultiplierOverride?: number;
  /** Per-recruiter override of the recruiter tier multiplier (optional). */
  readonly recruiterTierMultiplierOverride?: number;
}): FirstInvoiceAccrual {
  let signupTotal = 0;
  let activationTotal = 0;
  for (const li of input.lineItems) {
    if (li.amount <= 0) continue;
    if (li.kind === 'signup_fee') signupTotal += li.amount;
    if (li.kind === 'activation_fee') activationTotal += li.amount;
  }
  const splits = input.rules.firstInvoice;

  const agentFromSignup = Math.floor(signupTotal * splits.signupAgentSharePct);
  const agentFromActivation = Math.floor(activationTotal * splits.activationAgentSharePct);
  const recruiterFromSignup = Math.floor(signupTotal * splits.signupRecruiterSharePct);
  const recruiterFromActivation = Math.floor(activationTotal * splits.activationRecruiterSharePct);

  const agentMultiplier =
    input.agentTierMultiplierOverride ?? input.rules.tierMultipliers[input.agentTier] ?? 1;
  const recruiterMultiplier =
    input.recruiterTier === undefined
      ? 0 // No recruiter → no recruiter accrual regardless of splits
      : (input.recruiterTierMultiplierOverride ??
        input.rules.recruiterTierMultipliers[input.recruiterTier] ??
        1);

  const agentSen = Math.floor((agentFromSignup + agentFromActivation) * agentMultiplier);
  const recruiterSen =
    input.recruiterTier === undefined
      ? 0
      : Math.floor((recruiterFromSignup + recruiterFromActivation) * recruiterMultiplier);

  return {
    agentSen,
    recruiterSen,
    perKindSen: {
      agentFromSignup,
      agentFromActivation,
      recruiterFromSignup,
      recruiterFromActivation,
    },
  };
}

/**
 * Recurring recruiter share — what the recruiter earns on every paid
 * invoice for as long as the downstream operator stays active. Mirror
 * of the agent's recurring math but uses the recruiter's dedicated
 * `recruiterRecurringRate` + the recruiter's tier multiplier.
 *
 * Only `subscription` line items contribute — `extra_outlets` accrual
 * stays with the agent (operator growth credit) per the design memo.
 */
export interface RecruiterRecurringAccrual {
  readonly totalSen: number;
  readonly effectiveRate: number;
  readonly effectiveMultiplier: number;
  /** Sum of subscription-line amounts before rate + multiplier. */
  readonly subscriptionTotalSen: number;
}

export function computeRecruiterRecurringAccrual(input: {
  readonly lineItems: readonly Pick<InvoiceLineItem, 'kind' | 'amount'>[];
  readonly rules: CommissionRules;
  readonly recruiterTier: RecruiterTier;
  readonly tierMultiplierOverride?: number;
  readonly recurringRateOverride?: number;
}): RecruiterRecurringAccrual {
  const rate = input.recurringRateOverride ?? input.rules.recruiterRecurringRate;
  const multiplier =
    input.tierMultiplierOverride ?? input.rules.recruiterTierMultipliers[input.recruiterTier] ?? 1;
  let subscriptionTotal = 0;
  for (const li of input.lineItems) {
    if (li.amount <= 0) continue;
    if (li.kind === 'subscription') subscriptionTotal += li.amount;
  }
  const preMultiplier = Math.floor(subscriptionTotal * rate);
  const totalSen = Math.floor(preMultiplier * multiplier);
  return {
    totalSen,
    effectiveRate: rate,
    effectiveMultiplier: multiplier,
    subscriptionTotalSen: subscriptionTotal,
  };
}

// ── Sprint 95: one-time signup commission model ──────────────────────
//
// Both agent + recruiter now earn ONCE per operator signup, not on
// every paid invoice. The trigger is the operator's FIRST paid invoice
// (`agents/{aid}/operators/{tid}.firstPaidAt === null` flips to a
// timestamp); both ledger entries are written deterministically using
// doc id `signupCommission-{tenantId}` so a retried invoice payment
// can never double-pay.
//
// Math:
//   agent_total_sen     = floor(sum(subscription lines)
//                               * signupCommissionMultiplier
//                               * agentTierMultiplier)
//                         + signupBonus            ← flat, post-multiplier
//   recruiter_total_sen = floor(recruiterPerSignupSen
//                               * recruiterTierMultiplier)
//
// Recruiter math intentionally ignores the operator's subscription
// size — recruiters are paid a flat-per-downstream-signup so they're
// incentivised on agent throughput, not on agent-deal-size.

/**
 * Agent's one-time signup commission for an operator. Computed from the
 * subscription portion of the operator's FIRST paid invoice. Caller
 * is responsible for only invoking this once per operator (idempotent
 * via doc id `signupCommission-{tenantId}` at the write layer).
 */
export interface AgentSignupCommission {
  readonly totalSen: number;
  readonly subscriptionTotalSen: number;
  readonly effectiveMultiplier: number;
  readonly effectiveTierMultiplier: number;
  /**
   * Flat `rules.signupBonus` added AFTER the tier multiplier (ADR-0033).
   * Surfaced separately so the ledger entry's `basis` can show the agent
   * "one month of subscription + a RM X signing bonus" rather than one
   * opaque number. 0 when the platform has no bonus configured.
   */
  readonly bonusSen: number;
}

export function computeAgentSignupCommission(input: {
  readonly lineItems: readonly Pick<InvoiceLineItem, 'kind' | 'amount'>[];
  readonly rules: CommissionRules;
  readonly tier: AgentTier;
  readonly tierMultiplierOverride?: number;
  readonly multiplierOverride?: number;
  /** Per-agent override of the flat signing bonus (optional). */
  readonly signupBonusOverride?: number;
}): AgentSignupCommission {
  const multiplier = input.multiplierOverride ?? input.rules.signupCommissionMultiplier;
  const tierMultiplier =
    input.tierMultiplierOverride ?? input.rules.tierMultipliers[input.tier] ?? 1;
  // ADR-0033: a bonus is a BONUS, not a tier amplifier. It is added after the
  // tier multiplier so a gold agent's bonus equals a bronze agent's bonus —
  // multiplying it would quietly turn a flat incentive into a tier perk.
  // Defaults to 0, so every pre-ADR-0033 expectation is unchanged.
  const bonusSen = Math.max(0, Math.floor(input.signupBonusOverride ?? input.rules.signupBonus));
  let subscriptionTotal = 0;
  for (const li of input.lineItems) {
    if (li.amount <= 0) continue;
    if (li.kind === 'subscription') subscriptionTotal += li.amount;
  }
  const preMultiplier = Math.floor(subscriptionTotal * multiplier);
  const totalSen = Math.floor(preMultiplier * tierMultiplier) + bonusSen;
  return {
    totalSen,
    subscriptionTotalSen: subscriptionTotal,
    effectiveMultiplier: multiplier,
    effectiveTierMultiplier: tierMultiplier,
    bonusSen,
  };
}

/**
 * Recruiter's one-time per-downstream-signup commission. Flat sen amount
 * × recruiter tier multiplier. Doesn't look at invoice lines — the
 * incentive is on agent throughput, not deal size.
 */
export interface RecruiterSignupCommission {
  readonly totalSen: number;
  readonly basePerSignupSen: number;
  readonly effectiveTierMultiplier: number;
}

export function computeRecruiterSignupCommission(input: {
  readonly rules: CommissionRules;
  readonly recruiterTier: RecruiterTier;
  readonly tierMultiplierOverride?: number;
  readonly perSignupSenOverride?: number;
}): RecruiterSignupCommission {
  const base = input.perSignupSenOverride ?? input.rules.recruiterPerSignupSen;
  const tierMultiplier =
    input.tierMultiplierOverride ?? input.rules.recruiterTierMultipliers[input.recruiterTier] ?? 1;
  const totalSen = Math.floor(base * tierMultiplier);
  return {
    totalSen,
    basePerSignupSen: base,
    effectiveTierMultiplier: tierMultiplier,
  };
}

// ── ADR-0029: survival bonus (retention-aligned incentive) ───────────
//
// A SECOND one-time payment, fired when a signed operator is still paying
// `survivalBonusThresholdDays` after their first paid invoice. Re-points the
// agent from signing *volume* toward signing operators who *stay* — Kilat's
// only compounding revenue term — without recurring commission. Symmetric
// recruiter bonus for downstream survival.
//
// Math mirrors the signup commission so the agent's mental model is one rule
// ("a month of subscription, twice: once for signing, once for surviving"):
//   agent_total_sen     = floor(sum(subscription lines)
//                               * survivalBonusMultiplier
//                               * agentTierMultiplier)
//   recruiter_total_sen = floor(recruiterSurvivalBonusSen
//                               * recruiterTierMultiplier)
//
// Both default to 0 (off). The write layer keys the ledger entry on
// `survivalBonus-{tenantId}` so it is paid at most once per operator.

/**
 * Agent's one-time survival bonus for an operator that reached the retention
 * threshold. Computed from the subscription portion of the QUALIFYING invoice
 * (the paid invoice at/after the threshold that proves survival). Caller is
 * responsible for the once-per-operator guard (doc id `survivalBonus-{tenantId}`).
 */
export interface AgentSurvivalBonus {
  readonly totalSen: number;
  readonly subscriptionTotalSen: number;
  readonly effectiveMultiplier: number;
  readonly effectiveTierMultiplier: number;
}

export function computeAgentSurvivalBonus(input: {
  readonly lineItems: readonly Pick<InvoiceLineItem, 'kind' | 'amount'>[];
  readonly rules: CommissionRules;
  readonly tier: AgentTier;
  readonly tierMultiplierOverride?: number;
  readonly multiplierOverride?: number;
}): AgentSurvivalBonus {
  const multiplier = input.multiplierOverride ?? input.rules.survivalBonusMultiplier;
  const tierMultiplier =
    input.tierMultiplierOverride ?? input.rules.tierMultipliers[input.tier] ?? 1;
  let subscriptionTotal = 0;
  for (const li of input.lineItems) {
    if (li.amount <= 0) continue;
    if (li.kind === 'subscription') subscriptionTotal += li.amount;
  }
  const preMultiplier = Math.floor(subscriptionTotal * multiplier);
  const totalSen = Math.floor(preMultiplier * tierMultiplier);
  return {
    totalSen,
    subscriptionTotalSen: subscriptionTotal,
    effectiveMultiplier: multiplier,
    effectiveTierMultiplier: tierMultiplier,
  };
}

/**
 * Recruiter's one-time survival bonus when a downstream operator survives.
 * Flat sen × recruiter tier multiplier — doesn't look at invoice lines, same
 * as the recruiter signup commission (incentive is on agent throughput
 * quality, not deal size).
 */
export interface RecruiterSurvivalBonus {
  readonly totalSen: number;
  readonly baseSen: number;
  readonly effectiveTierMultiplier: number;
}

export function computeRecruiterSurvivalBonus(input: {
  readonly rules: CommissionRules;
  readonly recruiterTier: RecruiterTier;
  readonly tierMultiplierOverride?: number;
  readonly baseSenOverride?: number;
}): RecruiterSurvivalBonus {
  const base = input.baseSenOverride ?? input.rules.recruiterSurvivalBonusSen;
  const tierMultiplier =
    input.tierMultiplierOverride ?? input.rules.recruiterTierMultipliers[input.recruiterTier] ?? 1;
  const totalSen = Math.floor(base * tierMultiplier);
  return {
    totalSen,
    baseSen: base,
    effectiveTierMultiplier: tierMultiplier,
  };
}
